/**
 * SportsBrowserWidget — Browse all sports from TheOddsAPI
 * Extracted from: loadSports() lines 1966-2044
 * Shows: All sports grouped by category, with active/inactive filter
 * Collapsible groups, "Quick Odds" action buttons
 * API: GET /sports (FREE — no token cost)
 * CSS prefix: sbw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchOddsSports } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import { Globe, RefreshCw, ChevronRight, ChevronDown, Search, Eye, EyeOff } from 'lucide-react'
import { getLeagueLogoPath } from '../lib/teamLogos'
import './SportsBrowserWidget.css'

function SportsBrowserWidget({ onQuickOdds, onQuickEvents, onQuotaUpdate }) {
  const [sports, setSports] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  const [showInactive, setShowInactive] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [expanded, setExpanded] = useState(true)
  const [remaining, setRemaining] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    const res = await fetchOddsSports()

    if (res.status === 'ok' && res.data) {
      setSports(res.data)
      setLastFetch(new Date())
      if (res.remaining) setRemaining(res.remaining)
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)

      // Auto-expand all groups on first load
      const groups = new Set(res.data.map(s => s.group || 'Other'))
      setExpandedGroups(groups)
    } else {
      setError(res.message || 'Failed to load sports')
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Filter sports
  const filtered = sports.filter(s => {
    if (!showInactive && !s.active) return false
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      return (
        s.title?.toLowerCase().includes(term) ||
        s.key?.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term) ||
        s.group?.toLowerCase().includes(term)
      )
    }
    return true
  })

  // Group by category
  const grouped = {}
  filtered.forEach(sport => {
    const group = sport.group || 'Other'
    if (!grouped[group]) grouped[group] = []
    grouped[group].push(sport)
  })

  const sortedGroups = Object.keys(grouped).sort()
  const totalActive = sports.filter(s => s.active).length

  const toggleGroup = (group) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      return next
    })
  }

  const expandAll = () => setExpandedGroups(new Set(sortedGroups))
  const collapseAll = () => setExpandedGroups(new Set())

  return (
    <div className="widget-card">
      {/* Header */}
      <div
        className="widget-header sbw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Globe size={16} className="widget-icon" />
        <span className="widget-title">Sports Browser</span>
        <span className="sbw-count-badge">
          {totalActive} active{sports.length > totalActive ? ` / ${sports.length} total` : ''}
        </span>
        <span className="status-badge ok">FREE</span>
        <button
          className={`refresh-btn ${loading ? 'spinning' : ''}`}
          onClick={(e) => { e.stopPropagation(); fetchData() }}
          title="Refresh"
        >
          <RefreshCw size={12} />
        </button>
        <ChevronRight
          size={16}
          className={`sbw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Controls bar */}
          <div className="sbw-controls">
            <div className="sbw-search">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search sports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sbw-search-input"
              />
            </div>

            <div className="sbw-actions">
              <button
                className={`sbw-toggle-btn ${showInactive ? 'active' : ''}`}
                onClick={() => setShowInactive(v => !v)}
                title={showInactive ? 'Hide inactive' : 'Show inactive'}
              >
                {showInactive ? <Eye size={13} /> : <EyeOff size={13} />}
                <span>{showInactive ? 'All' : 'Active'}</span>
              </button>

              <button className="sbw-action-btn" onClick={expandAll} title="Expand all">
                <ChevronDown size={13} /> All
              </button>
              <button className="sbw-action-btn" onClick={collapseAll} title="Collapse all">
                <ChevronRight size={13} /> None
              </button>
            </div>
          </div>

          {/* Error state */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Loading state */}
          {loading && sports.length === 0 && (
            <div className="widget-body">
              <div className="widget-empty">Loading sports...</div>
            </div>
          )}

          {/* Sport groups */}
          {!error && filtered.length > 0 && (
            <div className="sbw-groups">
              {sortedGroups.map(group => {
                const items = grouped[group]
                const isOpen = expandedGroups.has(group)
                const icon = getSportIcon(items[0]?.key || '')

                return (
                  <div key={group} className="sbw-group">
                    <button
                      className="sbw-group-header"
                      onClick={() => toggleGroup(group)}
                    >
                      <ChevronRight
                        size={14}
                        className={`sbw-group-chevron ${isOpen ? 'rotated' : ''}`}
                      />
                      <span className="sbw-group-icon">{icon}</span>
                      <span className="sbw-group-name">{group}</span>
                      <span className="sbw-group-count">{items.length}</span>
                    </button>

                    {isOpen && (
                      <div className="sbw-group-content">
                        <table className="sbw-table">
                          <thead>
                            <tr>
                              <th>Sport</th>
                              <th>Key</th>
                              <th>Description</th>
                              <th>Status</th>
                              <th>Type</th>
                              {(onQuickOdds || onQuickEvents) && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map(sport => (
                              <tr key={sport.key} className={!sport.active ? 'sbw-inactive' : ''}>
                                <td className="sbw-sport-title">
                                  {getLeagueLogoPath(sport.key) && (
                                    <img
                                      className="sbw-league-logo"
                                      src={getLeagueLogoPath(sport.key)}
                                      alt=""
                                      width={16}
                                      height={16}
                                      onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                  )}
                                  <strong>{sport.title}</strong>
                                </td>
                                <td className="sbw-sport-key">{sport.key}</td>
                                <td className="sbw-sport-desc">{sport.description || '-'}</td>
                                <td>
                                  {sport.active
                                    ? <span className="sbw-status-active">Active</span>
                                    : <span className="sbw-status-inactive">Inactive</span>
                                  }
                                </td>
                                <td>
                                  {sport.has_outrights
                                    ? <span className="sbw-type-outrights">Outrights</span>
                                    : <span className="sbw-type-match">Match</span>
                                  }
                                </td>
                                {(onQuickOdds || onQuickEvents) && (
                                  <td className="sbw-actions-cell">
                                    {sport.active && !sport.has_outrights && onQuickOdds && (
                                      <button
                                        className="sbw-odds-btn"
                                        onClick={() => onQuickOdds(sport.key)}
                                      >
                                        Odds
                                      </button>
                                    )}
                                    {sport.active && onQuickEvents && (
                                      <button
                                        className="sbw-events-btn"
                                        onClick={() => onQuickEvents(sport.key)}
                                      >
                                        Events
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* No results */}
          {!loading && !error && filtered.length === 0 && sports.length > 0 && (
            <div className="widget-body">
              <div className="widget-empty">
                No sports match your search{!showInactive ? ' (try showing inactive)' : ''}.
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="widget-footer">
            <span>
              {lastFetch
                ? `Updated ${lastFetch.toLocaleTimeString()}`
                : 'Not loaded'}
            </span>
            <span>
              {remaining !== null && `API requests remaining: ${remaining}`}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export default SportsBrowserWidget
